/* Fantastick Standort V1.1.0 Embed – nur kostenlose Embed-Karten, keine Maps JavaScript API, keine Standort-Cookies. */
(() => {
  'use strict';
  const TAG = 'fantastick-location';
  if (customElements.get(TAG)) return;
  const parseCoordinate = (value, limit) => {
    const input = String(value ?? '').trim().replace(',', '.');
    if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(input)) return null;
    const number = Number(input);
    return Number.isFinite(number) && Math.abs(number) <= limit ? number : null;
  };
  const clamp = (value, min, max, fallback) => Number.isFinite(Number(value)) ? Math.min(max, Math.max(min, Number(value))) : fallback;
  const kilometres = (a, b) => {
    const rad = degrees => degrees * Math.PI / 180;
    const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 6371.0088 * 2 * Math.asin(Math.sqrt(Math.max(0, Math.min(1, h))));
  };
  class FantastickLocation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.run = (this.run || 0) + 1;
      this.geoRun = (this.geoRun || 0) + 1;
      this.frame = null;
      this.state = 'idle';
      try { this.config = JSON.parse(this.querySelector('[data-location-config]').getAttribute('data-json')); }
      catch { this.note('Die Abschnitt-Konfiguration ist ungültig. Bitte die Originaldatei erneut einfügen.'); return; }
      this.lat = parseCoordinate(this.config.latitude, 85);
      this.lng = parseCoordinate(this.config.longitude, 180);
      this.pointValid = this.lat !== null && this.lng !== null;
      this.loadButton = this.querySelector('[data-map-load]');
      this.unloadButton = this.querySelector('[data-map-unload]');
      this.placeholder = this.querySelector('[data-map-placeholder]');
      this.mapSlot = this.querySelector('[data-map-slot]');
      this.mapStatus = this.querySelector('[data-map-status]');
      this.mapShell = this.querySelector('[data-map-shell]');
      this.gestureHint = this.querySelector('[data-map-gesture]');
      this.distanceArea = this.querySelector('[data-distance-area]');
      this.distanceResult = this.querySelector('[data-distance-result]');
      this.distanceButton = this.querySelector('[data-distance-button]');
      this.clearButton = this.querySelector('[data-distance-clear]');
      this.initialDistanceText ||= this.distanceResult?.textContent || '';
      this.initialMapButton = this.loadButton?.textContent || 'Google-Karte laden';
      const listen = (node, type, fn) => node?.addEventListener(type, fn, { signal: this.controller.signal });
      if (this.loadButton && this.pointValid && this.config.apiKey?.trim()) this.loadButton.hidden = false;
      if (!this.pointValid) {
        this.note('Bitte gültige Koordinaten eintragen: Breitengrad −85 bis 85, Längengrad −180 bis 180. Die Beispiele in den Feldhinweisen sind kein bestätigter Betriebsstandort.');
        if (this.config.routeMode === 'area') this.querySelector('[data-route-link]')?.setAttribute('hidden', '');
      }
      if (this.distanceArea && this.pointValid) {
        this.distanceArea.hidden = false;
        this.distanceArea.classList.remove('has-distance');
        this.distanceResult.textContent = this.initialDistanceText;
        this.distanceButton.disabled = false;
        this.distanceButton.textContent = this.config.distanceButton || 'Entfernung anzeigen';
        this.clearButton.hidden = true;
      }
      listen(this.loadButton, 'click', () => this.loadMap());
      listen(this.unloadButton, 'click', () => this.unloadMap(true));
      listen(this.distanceButton, 'click', () => this.locate());
      listen(this.clearButton, 'click', () => this.clearDistance());
      listen(window, 'pagehide', () => {
        this.unloadMap(false);
        if (this.distanceArea) this.clearDistance(false);
      });
      listen(document, 'fantastick:maps-revoke', () => this.unloadMap(false));
      listen(document, 'visitorConsentCollected', event => {
        const d = event.detail || {};
        // Shopify: eine ausdrückliche Ablehnung aller drei Kategorien stoppt auch unsere Karte.
        // Keine automatische Freigabe aus Analytics-/Marketing-Cookies ableiten.
        if (d.analyticsAllowed === false && d.marketingAllowed === false && d.preferencesAllowed === false) this.unloadMap(false);
      });
    }
    disconnectedCallback() {
      this.run++;
      this.geoRun++;
      this.controller?.abort();
      this.controller = null;
      this.unloadMap(false);
    }
    note(message) {
      const node = this.querySelector('[data-editor-status]');
      if (node) node.textContent = message;
    }
    setMapStatus(message) { if (this.mapStatus) this.mapStatus.textContent = message; }
    consentEvent(allowed) {
      this.dispatchEvent(new CustomEvent('fantastick:location-map-consent', {
        bubbles: true,
        detail: { service: 'google-maps', allowed, sectionId: this.id, noticeVersion: '1.1.0', at: new Date().toISOString() }
      }));
    }
    buildEmbedURL() {
      // Fest vorgegebener Embed-Endpunkt. Keine Places-, Routes- oder Dynamic-Maps-API.
      const place = String(this.config.placeQuery || '').trim();
      const mode = this.config.approximate === false && place ? 'place' : 'view';
      const url = new URL(`https://www.google.com/maps/embed/v1/${mode}`);
      url.searchParams.set('key', this.config.apiKey.trim());
      url.searchParams.set('center', `${this.lat},${this.lng}`);
      url.searchParams.set('zoom', String(Math.round(clamp(this.config.zoom, 5, 17, 12))));
      url.searchParams.set('maptype', 'roadmap');
      url.searchParams.set('language', 'de');
      url.searchParams.set('region', 'DE');
      if (mode === 'place') url.searchParams.set('q', place);
      return url.href;
    }
    loadMap() {
      if (!this.isConnected || !this.pointValid || !this.config.apiKey?.trim() || this.state === 'loading' || this.state === 'ready') return;
      this.state = 'loading';
      const run = ++this.run;
      this.loadButton.disabled = true;
      this.loadButton.textContent = 'Karte wird geöffnet …';
      this.unloadButton.hidden = false;
      this.mapShell.setAttribute('aria-busy', 'true');
      this.mapShell.style.minHeight = `${Math.ceil(this.mapShell.getBoundingClientRect().height)}px`;
      this.setMapStatus('Google Maps wird geöffnet. Du kannst die Karte jederzeit wieder deaktivieren.');
      try {
        const iframe = document.createElement('iframe');
        iframe.title = `Interaktive Google-Karte: ${this.config.title || 'Fantastick'}`;
        iframe.width = '600';
        iframe.height = '450';
        iframe.setAttribute('allow', "fullscreen; geolocation 'none'");
        iframe.setAttribute('allowfullscreen', '');
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        // Nur der Klick erzeugt den Frame. Kein Vorabladen, kein externer Platzhalter.
        iframe.src = this.buildEmbedURL();
        const current = () => this.isConnected && this.run === run && this.frame === iframe;
        iframe.addEventListener('load', () => {
          if (!current() || this.state !== 'loading') return;
          clearTimeout(this.mapTimeout);
          this.state = 'ready';
          this.mapShell.setAttribute('aria-busy', 'false');
          this.placeholder.hidden = true;
          this.gestureHint.hidden = false;
          this.setMapStatus('');
          if (document.activeElement === this.loadButton) this.unloadButton.focus({ preventScroll: true });
          // Cross-Origin: load bedeutet Dokument geladen, nicht API-Key erfolgreich.
          // Google-Fehler innerhalb des Frames sind von außen nicht auslesbar.
        }, { once: true });
        iframe.addEventListener('error', () => {
          if (current()) this.mapFailed('Der Karten-Frame konnte nicht geöffnet werden.');
        }, { once: true });
        this.frame = iframe;
        this.consentEvent(true);
        // Ein synchroner CMP-Widerruf darf keinen anschließend eingefügten Frame hinterlassen.
        if (!current() || this.state !== 'loading') return;
        this.mapSlot.replaceChildren(iframe);
        this.mapTimeout = setTimeout(() => {
          if (current() && this.state === 'loading') this.mapFailed('Zeitüberschreitung beim Öffnen.');
        }, 25000);
      } catch { this.mapFailed('Bitte die Embed-Einstellungen prüfen.'); }
    }
    removeFrame() {
      clearTimeout(this.mapTimeout);
      this.mapTimeout = null;
      this.frame?.remove();
      this.frame = null;
    }
    mapFailed(reason) {
      this.removeFrame();
      this.state = 'error';
      this.consentEvent(false);
      this.placeholder.hidden = false;
      this.gestureHint.hidden = true;
      this.mapShell.setAttribute('aria-busy', 'false');
      if (this.loadButton) {
        this.loadButton.disabled = false;
        this.loadButton.textContent = 'Karte erneut laden';
      }
      this.setMapStatus('Die Karte konnte nicht geladen werden. Versuche es erneut oder nutze die Wegbeschreibung bzw. unser Anfrageformular.');
      this.note(`Kartenfehler: ${reason} Maps Embed API aktivieren; API- und Website-Beschränkungen des Schlüssels prüfen. Keine Maps JavaScript API erforderlich.`);
    }
    unloadMap(returnFocus) {
      const wasActive = this.state === 'loading' || this.state === 'ready' || this.state === 'error';
      this.run++;
      this.removeFrame();
      this.state = 'idle';
      if (!this.placeholder) return;
      this.placeholder.hidden = false;
      this.gestureHint.hidden = true;
      this.unloadButton.hidden = true;
      this.mapShell.setAttribute('aria-busy', 'false');
      this.mapShell.style.removeProperty('min-height');
      if (this.loadButton) {
        this.loadButton.disabled = false;
        this.loadButton.textContent = this.initialMapButton;
      }
      this.setMapStatus(wasActive ? 'Die Karte ist deaktiviert. Weitere Kartenabrufe dieses Abschnitts sind gestoppt.' : '');
      if (wasActive) this.consentEvent(false);
      if (returnFocus) this.loadButton?.focus({ preventScroll: true });
    }
    locate() {
      if (!this.pointValid || !this.distanceButton || this.distanceButton.disabled) return;
      if (!window.isSecureContext || !navigator.geolocation) {
        this.distanceResult.textContent = 'Dein Browser unterstützt hier keine Standortabfrage. Du kannst die Wegbeschreibung nutzen oder deinen Besuch direkt mit uns abstimmen.';
        return;
      }
      const run = ++this.geoRun;
      this.distanceButton.disabled = true;
      this.distanceButton.textContent = 'Standort wird ermittelt …';
      this.distanceArea.classList.remove('has-distance');
      this.distanceResult.textContent = 'Bitte erlaube die Standortabfrage in der Meldung deines Browsers.';
      this.clearButton.hidden = false;
      const failed = code => {
        if (!this.isConnected || this.geoRun !== run) return;
        this.distanceButton.disabled = false;
        this.distanceButton.textContent = this.config.distanceButton || 'Entfernung anzeigen';
        const messages = {
          1: 'Die Standortfreigabe wurde nicht erteilt. Du kannst weiterhin die Wegbeschreibung nutzen. Eine erneute Freigabe lässt sich in den Website-Einstellungen deines Browsers erlauben.',
          2: 'Dein Standort ist gerade nicht verfügbar. Prüfe die Standortdienste oder nutze die Wegbeschreibung.',
          3: 'Die Standortermittlung hat zu lange gedauert. Versuche es erneut oder nutze die Wegbeschreibung.',
          4: 'Dein Standort ist zu ungenau für eine hilfreiche Entfernungsangabe. Versuche es mit aktivierten Standortdiensten erneut oder nutze die Wegbeschreibung.'
        };
        this.distanceResult.textContent = messages[code] || messages[2];
      };
      try {
        navigator.geolocation.getCurrentPosition(position => {
          if (!this.isConnected || this.geoRun !== run) return;
          const coords = position.coords;
          const latitude = parseCoordinate(coords.latitude, 90), longitude = parseCoordinate(coords.longitude, 180);
          if (latitude === null || longitude === null || !Number.isFinite(coords.accuracy) || coords.accuracy > 5000 || coords.accuracy < 0) return failed(4);
          const distance = kilometres({ lat: latitude, lng: longitude }, { lat: this.lat, lng: this.lng });
          const number = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(Math.round(distance));
          const end = this.config.approximate ? 'bis in unsere Nähe' : 'zu uns';
          let message = distance < 1 ? `Unter 1 km Luftlinie ${end}.` : `Ca. ${number} km Luftlinie ${end}.`;
          if (coords.accuracy > 1000) message += ' Dein Gerätestandort ist nur grob bestimmt.';
          this.distanceResult.textContent = message;
          this.distanceArea.classList.add('has-distance');
          this.distanceButton.disabled = false;
          this.distanceButton.textContent = 'Entfernung neu berechnen';
          // Die Koordinaten werden weder gehalten noch an Frame, Google, Shopify oder Speicher übergeben.
        }, error => failed(error.code), { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 });
      } catch { failed(2); }
    }
    clearDistance(focus = true) {
      this.geoRun++;
      this.distanceResult.textContent = this.initialDistanceText;
      this.distanceArea.classList.remove('has-distance');
      this.distanceButton.disabled = false;
      this.distanceButton.textContent = this.config.distanceButton || 'Entfernung anzeigen';
      this.clearButton.hidden = true;
      if (focus) this.distanceButton.focus({ preventScroll: true });
    }
  }
  customElements.define(TAG, FantastickLocation);
  // Optionale CMP-Anbindung: nur Widerruf, keine stillschweigende Freigabe aus fremden Cookie-Kategorien.
  window.FantastickLocationConsent = Object.freeze({
    revoke: () => document.dispatchEvent(new CustomEvent('fantastick:maps-revoke'))
  });
})();
